import { isLocalConfirmationRequired } from '../../../hooks/useLocalBusinessConsent';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import {
  cashFailure,
  retainCashIntent,
  memberCashReference,
  type CashIntent,
} from './cashRecovery';
const PREFIX = 'cash-intent-v1:';
export function useCashScope(memberId = '') {
  return useAuthStore((s) => `${s.user?.id ?? ''}:${sessionVenueKey(s.accessToken)}:${memberId}`);
}
// Member cash keeps references without payload on scope loss; other cash retains its purge policy.
useAuthStore.subscribe((next, previous) => {
  if (
    next.sessionRevision !== previous.sessionRevision ||
    next.user?.id !== previous.user?.id ||
    (!!previous.accessToken &&
      sessionVenueKey(next.accessToken) !== sessionVenueKey(previous.accessToken)) ||
    [
      'accounting:read',
      'accounting:open',
      'accounting:edit',
      'accounting:close',
      'member:read',
      'member:adjust_points',
    ].some(
      (action) =>
        previous.allowedActions?.includes(action) && !next.allowedActions?.includes(action),
    )
  ) {
    try {
      for (const key of Object.keys(sessionStorage))
        if (key.startsWith(PREFIX)) {
          const value = JSON.parse(sessionStorage.getItem(key) ?? 'null');
          const reference = memberCashReference(value, Date.now());
          if (reference) sessionStorage.setItem(key, JSON.stringify(reference));
          else sessionStorage.removeItem(key);
        }
    } catch {
      /* No persistence is safer than replay. */
    }
  }
});
export function useCashIntent(
  scope: string,
  kind: 'opening' | 'counted-opening' | 'deposit' | 'workflow' | 'contract-upgrade',
) {
  const identity = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
    ]),
  );
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const signature = () => {
    const s = useAuthStore.getState();
    return JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
    ]);
  };
  const current = () => mounted.current && scopeRef.current === scope && signature() === identity;
  const storageKey = `${PREFIX}${scope}:${kind}`;
  const historyKey = `${storageKey}:history`;
  const [historical, setHistorical] = useState<CashIntent[]>(() => {
    try {
      const stored: unknown = JSON.parse(sessionStorage.getItem(historyKey) ?? '[]');
      return Array.isArray(stored)
        ? stored
            .flatMap((value) => {
              const retained = retainCashIntent(value, scope, Date.now());
              return retained?.stage === 'COMMITTED' && retained.businessDate ? [retained] : [];
            })
            .slice(-20)
        : [];
    } catch {
      return [];
    }
  });
  const [intent, setIntent] = useState<CashIntent | null>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const retained = retainCashIntent(JSON.parse(raw), scope, Date.now());
      if (retained) sessionStorage.setItem(storageKey, JSON.stringify(retained));
      else sessionStorage.removeItem(storageKey);
      return retained;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    return useAuthStore.subscribe(() => {
      if (!current()) {
        setIntent(null);
        setError('DENIED');
      }
    });
  }, [identity, scope]);
  useEffect(() => {
    setIntent((previous) => (previous?.scope === scope ? previous : null));
  }, [scope]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!intent) return;
    const prune = () => {
      if (!isCurrent(intent.operationKey)) return;
      const retained = retainCashIntent(intent, scope, Date.now());
      try {
        if (retained) sessionStorage.setItem(storageKey, JSON.stringify(retained));
        else sessionStorage.removeItem(storageKey);
      } catch {
        /* Retention cannot grant replay. */
      }
      if (!retained) {
        // Keep a local unresolved marker until the operator leaves; expiry never enables collection.
        setIntent({ ...intent, body: undefined });
      } else if (intent.body && !retained.body) setIntent(retained);
    };
    const timer = setInterval(prune, 1000);
    return () => clearInterval(timer);
  }, [intent, scope, storageKey]);
  const guarded = kind === 'workflow' || kind === 'deposit';
  // The expected key binds late callbacks to the slot they actually started from.
  function isCurrent(operationKey?: string) {
    if (!current()) return false;
    if (!guarded || operationKey === undefined) return true;
    try {
      const active = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') as CashIntent | null;
      return active?.scope === scope && active.operationKey === operationKey;
    } catch {
      return false;
    }
  }
  function committed(value: CashIntent) {
    if (!isCurrent(value.operationKey)) return false;
    const safe = { ...value, body: undefined, stage: 'COMMITTED' as const };
    setIntent(safe);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(safe));
    } catch {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* GET only in this tab. */
      }
    }
    return true;
  }
  function voided(value: CashIntent) {
    if (kind !== 'deposit') return false;
    if (!isCurrent(value.operationKey)) return false;
    save({ ...value, body: undefined, stage: 'VOIDED' });
    return true;
  }
  // Only the caller's authenticated receipt lookup may establish success/date.
  // Stored history is a lookup reference, never authority to open or replay.
  function archiveCommitted(value: CashIntent) {
    if (!current() || value.scope !== scope || value.stage !== 'COMMITTED' || !value.businessDate)
      return false;
    const safe = { ...value, body: undefined };
    const next = [...historical.filter((r) => r.operationKey !== value.operationKey), safe]
      .flatMap((r) => {
        const retained = retainCashIntent(r, scope, Date.now());
        return retained ? [retained] : [];
      })
      .slice(-20);
    try {
      sessionStorage.setItem(historyKey, JSON.stringify(next));
      const active = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') as CashIntent | null;
      if (active?.operationKey === value.operationKey) sessionStorage.removeItem(storageKey);
      setHistorical(next);
      setIntent((active) => (active?.operationKey === value.operationKey ? null : active));
      setError('');
      return true;
    } catch {
      setError('UNKNOWN');
      return false;
    }
  }
  function save(value: CashIntent, expectedKey?: string) {
    if (!current()) throw new Error('Scope changed');
    if (guarded) {
      const active = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') as CashIntent | null;
      if (
        active?.operationKey === value.operationKey &&
        ['COMMITTED', 'VOIDED'].includes(active.stage) &&
        value.stage !== active.stage
      )
        throw new Error('Completed operation is read only');
      if (active && active.operationKey !== value.operationKey) {
        const replacement =
          active.scope === scope &&
          active.operationKey === expectedKey &&
          (['COMMITTED', 'VOIDED'].includes(active.stage) ||
            (kind === 'deposit' &&
              active.action === 'ISSUE_DEPOSIT_SOURCE' &&
              value.action === 'CASH_DEPOSIT' &&
              value.stage === 'PREPARED' &&
              value.issuanceKey === active.operationKey));
        if (!replacement) throw new Error('Recovery slot changed');
      } else if (
        expectedKey &&
        active?.operationKey !== expectedKey &&
        active?.operationKey !== value.operationKey
      ) {
        throw new Error('Recovery slot changed');
      }
    }
    const retained = retainCashIntent(value, scope, Date.now());
    if (!retained) throw new Error('Expired recovery');
    sessionStorage.setItem(storageKey, JSON.stringify(retained));
    setIntent(retained);
    return retained;
  }
  async function run<T>(
    work: () => Promise<T>,
    expectedKey = intent?.operationKey,
  ): Promise<T | undefined> {
    if (inFlight.current || !current()) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    const before = useAuthStore.getState();
    const revision = before.sessionRevision;
    const venue = sessionVenueKey(before.accessToken);
    const permissions = JSON.stringify(before.allowedActions);
    try {
      const result = await work();
      if (
        mounted.current &&
        current() &&
        revision === useAuthStore.getState().sessionRevision &&
        venue === sessionVenueKey(useAuthStore.getState().accessToken) &&
        permissions === JSON.stringify(useAuthStore.getState().allowedActions)
      )
        return result;
    } catch (e) {
      if (mounted.current && current()) {
        const failure = cashFailure(e);
        if (failure === 'DENIED') {
          // A late denial belongs only to the operation that initiated this run.
          if (guarded && expectedKey && !isCurrent(expectedKey)) return;
          if (guarded && !expectedKey) {
            setError(failure);
            return;
          }
          setIntent(null);
          try {
            const reference = memberCashReference(
              JSON.parse(sessionStorage.getItem(storageKey) ?? 'null'),
              Date.now(),
            );
            if (reference) {
              sessionStorage.setItem(storageKey, JSON.stringify(reference));
              setIntent(reference);
            } else sessionStorage.removeItem(storageKey);
          } catch {
            /* No replay payload after denial. */
          }
        }
        setError(failure);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function post<T>(
    record: CashIntent,
    path: string,
    validate: (value: T) => boolean,
    onSuccess?: (result: T, record: CashIntent) => Promise<void> | void,
    expectedKey?: string,
    outgoingBody?: Record<string, unknown>,
  ): Promise<T | undefined> {
    return run(async () => {
      const retained = save(record, expectedKey); // Persistence must succeed before dispatch.
      if (!retained.body || ['COMMITTED', 'VOIDED'].includes(retained.stage))
        throw new Error('Read only recovery');
      let result: T;
      try {
        result = await api.post<T>(path, outgoingBody ?? retained.body);
      } catch (error) {
        if (retained.localBusinessProtocol === 1 && isLocalConfirmationRequired(error))
          save({ ...retained, localInitializationOutcome: 'KNOWN_REJECTED' });
        const response = (
          error as { response?: { status?: number; data?: { errorCode?: string } } }
        ).response;
        // Only an explicit pre-commit rejection permits a fresh intent. A lost response stays UNKNOWN.
        if (
          (kind === 'workflow' || kind === 'counted-opening') &&
          (response?.status === 400 || response?.status === 409) &&
          [
            'INVALID_REQUEST',
            'STALE_VERSION',
            'DAILY_SETTLEMENT_PROTOCOL_REQUIRED',
            'INSUFFICIENT_CASH',
            'INSUFFICIENT_ZIPA',
            'RECONCILIATION_REQUIRED',
            'SOURCE_UNRESOLVED',
            'REVIEW_NOTE_REQUIRED',
            'INITIAL_CASH_REQUIRED',
            'INITIAL_CASH_LOCKED',
            'CURRENT_OPEN',
            'CURRENT_DAY_CLOSED',
            'APPROVED_SOURCE_PENDING',
          ].includes(response.data?.errorCode ?? '')
        )
          save({ ...retained, stage: 'REJECTED' });
        throw error;
      }
      if (!isCurrent(record.operationKey)) return undefined;
      if (!validate(result)) throw new Error('Invalid receipt');
      await onSuccess?.(result, retained);
      return result;
    }, record.operationKey);
  }
  function startNew() {
    if (
      !intent ||
      !isCurrent(intent.operationKey) ||
      !['COMMITTED', 'REJECTED', 'VOIDED'].includes(intent.stage)
    )
      return;
    sessionStorage.removeItem(storageKey);
    setIntent(null);
    setError('');
  }
  return {
    intent: intent?.scope === scope ? intent : null,
    historical: historical.filter((r) => !!retainCashIntent(r, scope, Date.now())),
    archiveCommitted,
    isCurrent,
    save,
    committed,
    voided,
    startNew,
    error,
    setError,
    busy,
    run,
    post,
  };
}
